import {
  Component,
  OnDestroy,
  OnInit,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { Permission, UserDataTable } from '../users.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ROLES } from '@constants/user';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { PAGE, PAGES } from '@constants/constants';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { provideNativeDateAdapter } from '@angular/material/core';
import { PropertySelectOptionsQuery } from 'gql/graphql';
import { Apollo } from 'apollo-angular';
import { propertySelectOptionsQuery } from '@graphql/property';
import { getErrorMessage } from '@utils/errors';
import { ToastService } from '@services/ui/toast.service';
import { TranslateService } from '@ngx-translate/core';

const PERMISSION_KEY_MAP: Record<string, string> = {
  DASHBOARDS: 'Dashboards',
  PROPERTIES: 'Properties',
  ASSETMANAGEMENT: 'Asset Management',
  CONTROLS: 'Controls',
  PROPERTYSERVICES: 'Property Services',
  ALARMS: 'Alarms',
  REPORTS: 'Reports',
  SETTINGS: 'Settings',
};

@Component({
  selector: 'abb-user-form',
  standalone: true,
  template: `
    <form id="user-form" (ngSubmit)="onSubmit()">
      <div [formGroup]="form" class="flex flex-col gap-3">
        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Name' | t }}</mat-label>
          <input matInput formControlName="name" name="name" />
          @if(form.get('name')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Surname' | t }}</mat-label>
          <input matInput formControlName="lastName" name="lastName" />
          @if(form.get('lastName')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Email' | t }}</mat-label>
          <input
            matInput
            formControlName="email"
            name="email"
            [readonly]="saving()"
          />
          @if(form.get('email')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          } @if(form.get('email')?.hasError('email') ||
          form.get('email')?.hasError('pattern')){
          <mat-error>{{
            'Email is not valid (e.g., user@example.com)' | t
          }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Select Properties' | t }}</mat-label>
          <mat-select
            id="propertyIds"
            formControlName="propertyIds"
            multiple
            [panelClass]="'panel border-font-checkbox'"
          >
            @for (property of properties$ | async; track property.id) {
            <mat-option [id]="'property-' + property.id" [value]="property.id">
              {{ property.name }}
            </mat-option>
            }
          </mat-select>
          @if(form.get('propertyIds')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'Role' | t }}</mat-label>
          <mat-select formControlName="role" name="role" [panelClass]="'panel'">
            @for (option of options; track option) {
            <mat-option [value]="option.value">{{ option.label }}</mat-option>
            }
          </mat-select>
          @if(form.get('role')?.hasError('required')){
          <mat-error>{{ 'This field is required' | t }}</mat-error>
          }
        </mat-form-field>

        @if(form.value.role === 'COMMISSIONER'){
        <mat-form-field class="w-full" color="primary">
          <mat-label>{{ 'End validity date' | t }}</mat-label>
          <input
            matInput
            [matDatepicker]="endDatePicker"
            formControlName="commissionerEndDate"
            name="commissionerEndDate"
          />
          <mat-datepicker-toggle
            matIconSuffix
            [for]="endDatePicker"
          ></mat-datepicker-toggle>
          <mat-datepicker #endDatePicker [panelClass]="'panel'">
          </mat-datepicker>
        </mat-form-field>

        }
      </div>
      @if(form.value.role === 'COMMISSIONER' || form.value.role === 'ADVANCED'){
      <div>
        <table class="min-w-full overflow-auto">
          <thead>
            <tr class="uppercase">
              <th class="py-2 text-left">{{ 'Category' | t }}</th>
              <th class="py-2 text-center">{{ 'Viewer' | t }}</th>
              <th class="py-2 text-center">{{ 'Editing' | t }}</th>
            </tr>
          </thead>
          <tbody>
            @for (page of PAGES; track page) {
            <tr>
              <td class="text-left">{{ page.title }}</td>
              <td class="text-center">
                <mat-checkbox
                  [ngModel]="form.controls.commissionerPermissions.value?.[page.title]?.viewer"
                  (change)="viewerChange($event, page.title)"
                  [name]="'viewer_' + $index"
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
              <td class="text-center">
                <mat-checkbox
                  [ngModel]="form.controls.commissionerPermissions.value?.[page.title]?.editing"
                  (change)="editingChange($event, page.title)"
                  [name]="'editing_' + $index"
                  color="primary"
                  class="border-font-checkbox no-hover-checkbox"
                />
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>
      }
    </form>
  `,
  styles: ``,
  imports: [
    CommonModule,
    TPipe,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatCheckboxModule,
  ],
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'settings.tabs.users' },
    provideNativeDateAdapter(),
  ],
})
export class UserFormComponent implements OnInit, OnDestroy {
  readonly PAGES: Array<PAGE> = Object.values(PAGES);
  mode = input<'new' | 'edit'>();
  initValues = input<UserDataTable | undefined>();
  formValidationStatusChange = output<boolean>();
  formSubmit = output<FormGroup>();
  readonly options = Object.entries(ROLES).map(([key, value]) => {
    return {
      value: key,
      label: value,
    };
  });

  properties$ = new BehaviorSubject<PropertySelectOptionsQuery['propertyList']>(
    []
  );
  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [
      Validators.required,
      Validators.email,
      Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
    ]),
    propertyIds: new FormControl<(string | null)[]>([], [Validators.required]),
    role: new FormControl('', [Validators.required]),
    commissionerEndDate: new FormControl<Date | null>(
      { value: null, disabled: true },
      []
    ),
    commissionerPermissions: new FormControl<Record<PAGE['title'], Permission>>(
      {
        value: {
          Dashboards: { viewer: false, editing: false },
          Properties: { viewer: false, editing: false },
          ['Asset Management']: { viewer: false, editing: false },
          Controls: { viewer: false, editing: false },
          ['Property Services']: { viewer: false, editing: false },
          Alarms: { viewer: false, editing: false },
          Reports: { viewer: false, editing: false },
          Settings: { viewer: false, editing: false },
        },
        disabled: true,
      },
      []
    ),
  });

  private subscriptions: Subscription[] = [];
  constructor(
    private readonly apollo: Apollo,
    private readonly toast: ToastService,
    private readonly translate: TranslateService
  ) {
    this.subscriptions.push(
      this.form.statusChanges.subscribe(() => {
        this.formValidationStatusChange.emit(!this.form.valid);
      }),
      this.form.controls.role.valueChanges.subscribe((role) => {
        if (role === 'COMMISSIONER') {
          this.form.get('commissionerEndDate')?.enable();
          this.form.get('commissionerPermissions')?.enable();
        } else if (role === 'ADVANCED') {
          this.form.get('commissionerEndDate')?.disable();
          this.form.get('commissionerPermissions')?.enable();
        } else {
          this.form.get('commissionerEndDate')?.disable();
          this.form.get('commissionerPermissions')?.disable();
        }
      }),
      apollo
        .watchQuery<PropertySelectOptionsQuery>({
          query: propertySelectOptionsQuery,
        })
        .valueChanges.subscribe({
          next: ({ data }) => {
            this.properties$.next(data.propertyList);
          },
          error: (err) => {
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  editingChange(event: MatCheckboxChange, title: string) {
    const newValue = event.checked;
    const currentValue = this.form.get('commissionerPermissions')?.value;
    const viewerValue =
      newValue === true ? newValue : !!currentValue?.[title].viewer;
    this.form.patchValue({
      commissionerPermissions: {
        ...currentValue,
        [title]: { viewer: viewerValue, editing: newValue },
      },
    });
    this.form.updateValueAndValidity();
  }

  viewerChange(event: MatCheckboxChange, title: string) {
    const newValue = event.checked;
    const currentValue = this.form.get('commissionerPermissions')?.value;
    console.log(currentValue);

    const editingValue =
      currentValue?.[title].editing === true && newValue === false
        ? newValue
        : !!currentValue?.[title].editing;
    this.form.patchValue({
      commissionerPermissions: {
        ...currentValue,
        [title]: { viewer: newValue, editing: editingValue },
      },
    });
    this.form.updateValueAndValidity();
  }

  // Funzione helper per il merge delle permissions

  private mergePermissions(
    defaultPermissions: Record<string, Permission>,
    initPermissions: Record<string, Permission> | null | undefined
  ): Record<string, Permission> {
    if (!initPermissions) {
      return defaultPermissions;
    }

    const merged = { ...defaultPermissions };

    Object.keys(initPermissions).forEach((initKey) => {
      const mappedKey = PERMISSION_KEY_MAP[initKey];
      if (mappedKey && merged[mappedKey]) {
        merged[mappedKey] = {
          ...merged[mappedKey],
          ...initPermissions[initKey],
        };
      }
    });

    return merged;
  }

  ngOnInit(): void {
    if (this.mode() == 'edit') {
      const defaultPermissions =
        this.form.get('commissionerPermissions')?.value || {};
      const initPermissions = this.initValues()?.commissionerPermissions;

      this.form.setValue({
        name: this.initValues()?.name!,
        lastName: this.initValues()?.lastName!,
        email: this.initValues()?.email!,
        propertyIds: [],
        role: this.options.find((o) => o.label === this.initValues()?.role!)
          ?.value!,
        commissionerEndDate: this.initValues()?.commissionerEndDate ?? null,
        commissionerPermissions: this.mergePermissions(
          defaultPermissions,
          initPermissions
        ),
      });

      this.properties$.asObservable().subscribe((properties) =>
        this.form.patchValue({
          propertyIds: properties
            .filter((p) => this.initValues()?.propertyIds!.includes(p.id))
            ?.map((p) => p.id),
        })
      );

      this.form.get('email')?.disable();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  saving = signal(false);
  onSubmit(): void {
    this.saving.set(true);
    this.form.get('email')?.enable();
    this.formSubmit.emit(this.form);
  }
}
